import "$styles/index.css"
import "./delight.js"

// Import all JavaScript & CSS files from src/_components
import components from "$components/**/*.{js,jsx,js.rb,css}"

import posthog from "posthog-js"

// POSTHOG_PROJECT_TOKEN is injected at build time by esbuild.config.js. It's a public
// key by design — it identifies the project for ingestion and grants no read access.
const POSTHOG_TOKEN = process.env.POSTHOG_PROJECT_TOKEN

// Attribution params we carry across navigations. Traffic from the HOA Essentials
// YouTube channel arrives at /resources/<slug>/?video=VIDEO_ID, and we want that id
// to survive through to whatever the visitor clicks — mainly the Building Health
// Score — so we can trace which episode drove a run.
//
// Allowlisted rather than copied wholesale, so arbitrary query strings don't get
// pushed into app.nestingbird.co.
const FORWARDED_PARAMS = ["video", "ref", "utm_source", "utm_medium", "utm_campaign", "utm_content"]

const incomingParams = () => {
  const current = new URLSearchParams(window.location.search)
  const kept = new URLSearchParams()
  FORWARDED_PARAMS.forEach((key) => {
    const value = current.get(key)
    if (value) kept.set(key, value)
  })
  return kept
}

// Opt-in per link via [data-forward-params]. Applied to internal navigation and to
// our own app; deliberately NOT applied to PDF downloads, Google Drive copy links, or
// YouTube — no attribution value there, and it would leak our params to third parties.
const forwardParamsToLinks = () => {
  const params = incomingParams()
  if ([...params].length === 0) return

  document.querySelectorAll("a[data-forward-params][href]").forEach((link) => {
    let url
    try {
      url = new URL(link.getAttribute("href"), window.location.origin)
    } catch {
      return
    }
    // A param already on the href wins — the markup was explicit about it.
    params.forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value)
    })
    link.setAttribute("href", url.toString())
  })
}

// Analytics runs on the real site only; localhost would pollute the project with our
// own dev traffic.
if (POSTHOG_TOKEN && window.location.hostname !== "localhost") {
  posthog.init(POSTHOG_TOKEN, {
    // Ingestion is reverse-proxied through Netlify (see the /ph/* rules in
    // netlify.toml). Requests are first-party, so the CSP's `connect-src 'self'`
    // covers them without loosening and ad blockers don't recognize the endpoint.
    api_host: "/ph",
    // Where "view in PostHog" links point. Not used for ingestion.
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    // Session replay spins up its worker from a blob: URL, which the CSP's
    // `default-src 'self'` blocks — it would fail silently rather than error. To turn
    // replay on, add `worker-src 'self' blob:` to the CSP in netlify.toml first.
    disable_session_recording: true,
  })

  // Bundled as a module, so posthog isn't global by default. Exposing it makes the
  // install verifiable from the console on the live site (`posthog.config.token`).
  window.posthog = posthog

  // CTA click tracking. `data-track` labels are already on every CTA across the site.
  document.addEventListener("click", (event) => {
    const el = event.target.closest("[data-track]")
    if (!el) return
    posthog.capture("cta_click", {
      label: el.dataset.track,
      page: window.location.pathname,
      href: el.getAttribute("href") || undefined,
      // Episode attribution for traffic arriving from the YouTube channel.
      video: new URLSearchParams(window.location.search).get("video") || undefined,
    })
  })
}

// Copy-to-clipboard for blocks like the agent heads-up note on /for_agents/.
// The button names its source (an element id) and a status element that announces
// the result to screen readers; the visible label flips briefly so sighted users get
// the same confirmation. Falls back to a plain "select and copy" message where the
// Clipboard API is unavailable (insecure context, older browsers).
const bindCopyButtons = () => {
  document.querySelectorAll("[data-copy-button]").forEach((button) => {
    const source = document.getElementById(button.dataset.copyButton)
    const status = document.getElementById(button.dataset.copyStatus)
    const labelEl = button.querySelector("[data-copy-label-text]")
    if (!source) return

    let resetTimer
    const announce = (message, done) => {
      if (status) status.textContent = message
      if (labelEl && done) {
        labelEl.textContent = button.dataset.copyDone
        clearTimeout(resetTimer)
        resetTimer = setTimeout(() => {
          labelEl.textContent = button.dataset.copyLabel
        }, 2000)
      }
    }

    button.addEventListener("click", async () => {
      const text = source.innerText
      try {
        await navigator.clipboard.writeText(text)
        announce(button.dataset.copyStatusDone, true)
      } catch {
        // Leave the text selected so a manual copy is one keystroke away.
        const range = document.createRange()
        range.selectNodeContents(source)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        announce(button.dataset.copyStatusFailed, false)
      }
    })
  })
}

// Navbar scroll behavior: transparent → solid bg
document.addEventListener("DOMContentLoaded", () => {
  // Runs on every page and independently of the navbar, so it must come first.
  forwardParamsToLinks()
  bindCopyButtons()

  const nav = document.querySelector("[data-navbar]")
  if (!nav) return

  const onScroll = () => {
    if (window.scrollY > 10) {
      nav.classList.add("bg-white/95", "backdrop-blur-sm", "border-b", "border-gray-200")
      nav.classList.remove("bg-transparent", "border-transparent")
    } else {
      nav.classList.remove("bg-white/95", "backdrop-blur-sm", "border-b", "border-gray-200")
      nav.classList.add("bg-transparent", "border-transparent")
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true })
  onScroll()

  // Dropdown toggle
  document.querySelectorAll("[data-dropdown-toggle]").forEach((button) => {
    button.addEventListener("click", (e) => {
      e.stopPropagation()
      const menu = button.nextElementSibling
      if (!menu) return
      // Close all other dropdowns first
      document.querySelectorAll("[data-dropdown-menu]").forEach((m) => {
        if (m !== menu) m.classList.add("hidden")
      })
      menu.classList.toggle("hidden")
    })
  })

  // Close dropdowns when clicking outside
  document.addEventListener("click", () => {
    document.querySelectorAll("[data-dropdown-menu]").forEach((m) => m.classList.add("hidden"))
  })

  // Mobile menu toggle
  const mobileToggle = document.querySelectorAll("[data-mobile-toggle]")
  const mobileMenu = document.querySelector("[data-mobile-menu]")
  if (mobileMenu) {
    mobileToggle.forEach((btn) => {
      btn.addEventListener("click", () => {
        mobileMenu.classList.toggle("hidden")
        document.body.classList.toggle("overflow-hidden", !mobileMenu.classList.contains("hidden"))
      })
    })
  }

  // FAQ category filter
  const faqFilterButtons = document.querySelectorAll("[data-faq-filter]")
  const faqSections = document.querySelectorAll("[data-faq-section]")
  if (faqFilterButtons.length && faqSections.length) {
    const activeClasses = ["bg-primary/10", "text-primary"]
    const inactiveClasses = ["bg-gray-100", "text-gray-700"]

    const setActiveCategory = (category) => {
      faqFilterButtons.forEach((btn) => {
        const isActive = btn.dataset.faqFilter === category
        btn.classList.remove(...(isActive ? inactiveClasses : activeClasses))
        btn.classList.add(...(isActive ? activeClasses : inactiveClasses))
        btn.setAttribute("aria-pressed", isActive ? "true" : "false")
      })
      faqSections.forEach((section) => {
        section.classList.toggle("hidden", section.dataset.faqSection !== category)
      })
    }

    faqFilterButtons.forEach((btn) => {
      btn.addEventListener("click", () => setActiveCategory(btn.dataset.faqFilter))
    })

    // Respect URL hash on load (e.g., /faq#payments)
    const validCategories = Array.from(faqFilterButtons).map((b) => b.dataset.faqFilter)
    const initial = window.location.hash?.slice(1)
    if (initial && validCategories.includes(initial)) {
      setActiveCategory(initial)
    }
  }
})

// Feature-page "What you get" grid: pack the tinted capability cards to their own
// height instead of stretching every card in a row to the tallest one.
//
// The trick is a very short row track (CAP_ROW) plus a per-card row-span measured
// from its real rendered height, so a card occupies only the rows it needs and
// `row dense` backfills the gaps. Enhancement only — the markup renders as a plain
// equal-height grid, and this returns it to that state below the md breakpoint,
// where the layout is a single column and there is nothing to pack.
const CAP_ROW = 4
const CAP_GAP = 18

const capabilityMasonry = () => {
  const grids = document.querySelectorAll("[data-cap-grid]")
  if (!grids.length || !window.ResizeObserver) return

  // Matches Tailwind's md breakpoint, where the grid becomes 3 columns.
  const wide = window.matchMedia("(min-width: 768px)")

  grids.forEach((grid) => {
    const cells = Array.from(grid.querySelectorAll("[data-cap-cell]"))
    if (!cells.length) return
    let last = ""

    const clear = () => {
      grid.style.gridAutoRows = ""
      grid.style.gridAutoFlow = ""
      grid.style.rowGap = ""
      cells.forEach((cell) => {
        cell.style.gridRowEnd = ""
      })
      last = ""
    }

    const measure = () => {
      if (!wide.matches) {
        if (last !== "") clear()
        return
      }
      grid.style.gridAutoRows = `${CAP_ROW}px`
      grid.style.gridAutoFlow = "row dense"
      grid.style.rowGap = `${CAP_GAP}px`

      const spans = cells.map((cell) => {
        const card = cell.firstElementChild
        if (!card) return 1
        // +2 covers the card's 1px border on each edge, which the content box
        // height below does not include.
        const h = card.getBoundingClientRect().height + 2
        return Math.max(1, Math.ceil((h + CAP_GAP) / (CAP_ROW + CAP_GAP)))
      })

      // Bail before writing when nothing moved, so the ResizeObserver this runs
      // inside can't feed itself.
      const key = spans.join(",")
      if (key === last) return
      last = key
      cells.forEach((cell, i) => {
        cell.style.gridRowEnd = `span ${spans[i]}`
      })
    }

    const ro = new ResizeObserver(measure)
    // The grid itself is observed alongside the cards: its width changes the
    // moment the column count does, which is the one signal that reliably
    // catches a resize across the md breakpoint. Watching only the cards misses
    // it, and the grid would stay un-packed after a phone-to-desktop resize.
    ro.observe(grid)
    cells.forEach((cell) => {
      if (cell.firstElementChild) ro.observe(cell.firstElementChild)
    })
    wide.addEventListener("change", measure)
    window.addEventListener("resize", measure)
    // Web fonts land after first paint and change every card's height.
    if (document.fonts?.ready) document.fonts.ready.then(measure)
    measure()
  })
}

document.addEventListener("DOMContentLoaded", capabilityMasonry)
